import React from "react";
import { Box, Card, Chip, Grid, LinearProgress, Typography } from "@mui/material";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import TimelineOutlinedIcon from "@mui/icons-material/TimelineOutlined";
import { mapScheduleKpis } from "view/dashboard/schedule/scheduleKpiMapper";
import { enterpriseTokens } from "styles/theme";

const ScheduleStatisticsKpi = ({ kpiData }) => {
    const row = kpiData?.data?.[0];
    if (!row) return null;

    const kpiArray = mapScheduleKpis(row);
    const progressKpi = kpiArray.find((item) => item.type === "progress");
    const dateKpis = kpiArray.filter((item) => item.label?.toLowerCase().includes("date"));
    const daysToComplete = kpiArray.find((item) => item.label === "Days to Complete");
    const scheduleVariance = kpiArray.find((item) => item.label === "Schedule Variance");

    const actual = Number(progressKpi?.actual || 0);
    const plan = Number(progressKpi?.plan || 0);
    const progressVsPlan = plan > 0 ? Math.min((actual / plan) * 100, 100) : 0;
    const delta = actual - plan;
    const statusTone =
        delta >= 0
            ? { label: "Ahead of plan", color: enterpriseTokens.success, bg: "#DCFCE7" }
            : { label: "Behind plan", color: enterpriseTokens.danger, bg: "#FEE2E2" };

    return (
        <Card
            elevation={0}
            sx={{
                borderRadius: "3px",
                border: `1px solid ${enterpriseTokens.lineSoft}`,
                p: 0,
                width: "100%",
                backgroundColor: "#FFFFFF",
                overflow: "hidden",
            }}
        >
            <Box
                sx={{
                    px: 2,
                    py: 1.75,
                    borderBottom: `1px solid ${enterpriseTokens.lineSoft}`,
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 2,
                    alignItems: { xs: "flex-start", md: "center" },
                    flexDirection: { xs: "column", md: "row" },
                }}
            >
                <Box>
                    <Typography
                        sx={{
                            fontFamily: "Inter, Outfit, sans-serif",
                            fontWeight: 700,
                            fontSize: "18px",
                            lineHeight: "24px",
                            color: enterpriseTokens.navy,
                        }}
                    >
                        Schedule Intelligence
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: "Inter, Outfit, sans-serif",
                            fontWeight: 500,
                            fontSize: "13px",
                            lineHeight: "20px",
                            color: enterpriseTokens.muted,
                            mt: 0.25,
                        }}
                    >
                        Key delivery dates, progress variance, and forecast signals in one view.
                    </Typography>
                </Box>
                <Chip
                    label={statusTone.label}
                    sx={{
                        bgcolor: statusTone.bg,
                        color: statusTone.color,
                        borderRadius: "999px",
                        fontWeight: 600,
                        fontSize: "12px",
                    }}
                />
            </Box>

            <Grid container spacing={0}>
                <Grid size={{ xs: 12, md: 5 }}>
                    <Box
                        sx={{
                            height: "100%",
                            p: 2,
                            borderRight: { md: `1px solid ${enterpriseTokens.lineSoft}` },
                            backgroundColor: "#F8FAFC",
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                            <Box
                                sx={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: "3px",
                                    bgcolor: enterpriseTokens.primary,
                                    color: "#FFFFFF",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <TimelineOutlinedIcon fontSize="small" />
                            </Box>
                            <Box>
                                <Typography
                                    sx={{
                                        fontSize: "11px",
                                        fontWeight: 600,
                                        color: enterpriseTokens.muted,
                                        textTransform: "uppercase",
                                    }}
                                >
                                    Project Progress
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: "28px",
                                        lineHeight: "34px",
                                        fontWeight: 700,
                                        color: enterpriseTokens.navy,
                                    }}
                                >
                                    {actual.toFixed(2)}%
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.75 }}>
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: enterpriseTokens.slate }}>
                                Actual vs Plan
                            </Typography>
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: enterpriseTokens.muted }}>
                                Plan {plan.toFixed(2)}%
                            </Typography>
                        </Box>
                        <LinearProgress
                            variant="determinate"
                            value={progressVsPlan}
                            sx={{
                                height: 10,
                                borderRadius: "999px",
                                bgcolor: "#E2E8F0",
                                "& .MuiLinearProgress-bar": {
                                    borderRadius: "999px",
                                    bgcolor: statusTone.color,
                                },
                            }}
                        />
                        <Typography sx={{ mt: 1, fontSize: 12, fontWeight: 700, color: statusTone.color }}>
                            {Math.abs(delta).toFixed(2)}% {delta >= 0 ? "ahead" : "behind"} against planned progress
                        </Typography>
                    </Box>
                </Grid>

                <Grid size={{ xs: 12, md: 7 }}>
                    <Box sx={{ p: 2 }}>
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
                                gap: 1.25,
                                mb: 1.25,
                            }}
                        >
                            {dateKpis.map((item, index) => (
                                <Box
                                    key={item.label}
                                    sx={{
                                        p: 1.5,
                                        minHeight: 104,
                                        borderRadius: "3px",
                                        border: `1px solid ${enterpriseTokens.lineSoft}`,
                                        backgroundColor: index === 2 ? "#EFF6FF" : "#FFFFFF",
                                    }}
                                >
                                    <CalendarMonthOutlinedIcon sx={{ fontSize: 18, color: enterpriseTokens.primary, mb: 1 }} />
                                    <Typography
                                        sx={{
                                            fontSize: 11,
                                            fontWeight: 600,
                                            color: enterpriseTokens.muted,
                                            textTransform: "uppercase",
                                        }}
                                    >
                                        {item.label}
                                    </Typography>
                                    <Typography sx={{ mt: 0.75, fontSize: 18, fontWeight: 700, color: enterpriseTokens.navy }}>
                                        {item.value}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>

                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
                                gap: 1.25,
                            }}
                        >
                            {[daysToComplete, scheduleVariance].filter(Boolean).map((item, index) => (
                                <Box
                                    key={item.label}
                                    sx={{
                                        p: 1.5,
                                        borderRadius: "3px",
                                        border: `1px solid ${enterpriseTokens.lineSoft}`,
                                        borderLeft: `4px solid ${index === 0 ? enterpriseTokens.accent : statusTone.color}`,
                                        backgroundColor: "#FFFFFF",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 2,
                                    }}
                                >
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontSize: 11,
                                                fontWeight: 600,
                                                color: enterpriseTokens.muted,
                                                textTransform: "uppercase",
                                            }}
                                        >
                                            {item.label}
                                        </Typography>
                                        {item.subText && (
                                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: enterpriseTokens.muted, mt: 0.3 }}>
                                                {item.subText}
                                            </Typography>
                                        )}
                                    </Box>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <FlagOutlinedIcon sx={{ color: index === 0 ? enterpriseTokens.accent : statusTone.color }} />
                                        <Typography sx={{ fontSize: 22, fontWeight: 700, color: enterpriseTokens.navy, whiteSpace: "nowrap" }}>
                                            {item.value}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        </Card>
    );
};

export default ScheduleStatisticsKpi;
